import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { finalize, map, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { BaseModalComponent } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Roll } from '@app/models/knitting/roll';
import { WorkOrder, WorkOrderSearchCommand } from '@app/models/order/work-order';
import { WorkOrderType } from '@app/models/production/production-dashboard';
import { WorkOrderProxyService } from '@app/proxies/order/work-order.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';

export type AddFromGreigeModalResult = Roll[];

@Component({
  selector: 'app-add-from-greige-modal',
  templateUrl: './add-from-greige-modal.component.html',
  styleUrl: './add-from-greige-modal.component.scss',
})
export class AddFromGreigeModalComponent extends BaseModalComponent<AddFromGreigeModalResult | null> implements OnInit {
  private readonly individualProductionCardService = inject(IndividualProductionCardService);
  private readonly productionCardProxyService = inject(ProductionCardsProxyService);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly workOrderProxyService = inject(WorkOrderProxyService);
  private readonly translate = inject(TranslateService);

  isLoading = signal<number>(0);
  eligibleRollsFromGreige = signal<Roll[]>([]);
  selectedRollsToReAdd = signal<Roll[]>([]);
  reAddRollFilter = signal<string>('');

  reAddUseOtherWorkOrder = signal<boolean>(false);
  reAddSelectedWorkOrderRef = signal<string | null>(null);

  filteredEligibleRollsFromGreige = computed<Roll[]>(() => {
    const filter = this.reAddRollFilter().toLowerCase().trim();
    if (!filter) {
      return this.eligibleRollsFromGreige();
    }
    return this.eligibleRollsFromGreige().filter((roll) => roll.number?.toLowerCase().includes(filter));
  });

  ngOnInit(): void {
    this.fetchReAddRolls();
  }

  private fetchReAddRolls(): void {
    const pc = this.individualProductionCardService.getProductionCardOrThrow();
    const workOrderRef = this.reAddSelectedWorkOrderRef() ?? pc.workOrderReference!;

    this.isLoading.update((n) => n + 1);
    this.rollProxyService.getCandidateRollsByWorkOrder(workOrderRef, pc.id!).subscribe({
      next: (response) => {
        this.isLoading.update((n) => n - 1);
        if (response.success) {
          this.eligibleRollsFromGreige.set(response.data ?? []);
          this.selectedRollsToReAdd.set([]);
        }
      },
      error: () => this.isLoading.update((n) => n - 1),
    });
  }

  public fetchReAddWorkOrders = (query: string): Observable<WorkOrder[]> => {
    const command: WorkOrderSearchCommand = { orderNumber: query };
    this.isLoading.update((n) => n + 1);

    return this.workOrderProxyService.search(command, 1, 50).pipe(
      finalize(() => this.isLoading.update((n) => n - 1)),
      map((response) => (response.data?.items ?? []).filter((wo) => wo.orderType !== WorkOrderType.GreigeOnly)),
    );
  };

  onReAddWorkOrderSelect(event: AutoCompleteSelectEvent): void {
    const wo = event.value as WorkOrder;
    this.reAddSelectedWorkOrderRef.set(wo.id!);
    this.eligibleRollsFromGreige.set([]);
    this.selectedRollsToReAdd.set([]);
    this.fetchReAddRolls();
  }

  onAddSelected(): void {
    const rollsToReAdd = this.selectedRollsToReAdd();
    if (rollsToReAdd.length === 0) {
      GlobalMessageUtil.warn(
        this.translate.instant('Common_NoSelection'),
        this.translate.instant('Production_Card_Move_To_Batch_SelectRollsToAdd'),
      );
      return;
    }

    const pc = this.individualProductionCardService.getProductionCardOrThrow();
    const rollNumbers = rollsToReAdd.map((r) => r.number!);

    this.isLoading.update((n) => n + 1);
    this.productionCardProxyService.reAddRollsFromGreigeToBatching(pc.id!, rollNumbers).subscribe({
      next: (response) => {
        this.isLoading.update((n) => n - 1);
        if (response.success && response.data) {
          GlobalMessageUtil.success(
            this.translate.instant('Production_Card_Move_To_Batch_ReAddFromGreige'),
            `${rollNumbers.length} roll(s) re-added to Batching`,
          );
          this.close({ action: 'save', data: response.data });
        } else {
          GlobalMessageUtil.error(this.translate.instant('Common_Error'), this.translate.instant('Common_OperationFailed'));
        }
      },
      error: () => this.isLoading.update((n) => n - 1),
    });
  }

  onCancel(): void {
    this.close({ action: 'close', data: null });
  }
}
